import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack } from "@astryxdesign/core/Layout";
import { VStack } from "@astryxdesign/core/VStack";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import type { Device, SyncResult } from "../../lib/api";

interface DeviceSyncDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  device: Device | null;
  result: SyncResult | null;
  /** Opens the import wizard for the channels this sync found but did not add. */
  onImportNew: () => void;
}

/**
 * What a sync did, and what it left for the operator to decide.
 *
 * The dialog exists because a sync's most important output is the part it did
 * NOT act on. A toast saying "synced" would hide the two things worth reading:
 * channels the recorder has gained, and cameras it has stopped reporting.
 */
export function DeviceSyncDialog({ isOpen, onOpenChange, device, result, onImportNew }: DeviceSyncDialogProps) {
  const renamed = result?.renamed ?? [];
  const keptLocal = result?.kept_local_names ?? [];
  const newChannels = result?.new_channels ?? [];
  const missing = result?.missing_channels ?? [];
  const learnedSubStreams = result?.learned_sub_streams ?? 0;

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="info" width={560}>
      <Layout
        header={
          <DialogHeader
            title={device ? `Synced ${device.name}` : "Synced"}
            subtitle="Names are updated from the recorder. Cameras are never added or removed by a sync."
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <VStack gap={3}>
              {renamed.length > 0 && (
                <VStack gap={1}>
                  <Text type="label" color="secondary">
                    Renamed from the recorder
                  </Text>
                  {renamed.map((r) => (
                    <HStack key={r.camera_id} gap={1.5} vAlign="center" wrap="wrap">
                      <Token color="default" label={`ch${r.channel_id}`} size="sm" />
                      <Text type="supporting" color="secondary">
                        {r.old_name}
                      </Text>
                      <Text type="supporting">→ {r.new_name}</Text>
                    </HStack>
                  ))}
                </VStack>
              )}

              {learnedSubStreams > 0 && (
                // The only visible outcome on a recorder whose labels have not
                // changed, and worth saying plainly: these cameras can now be
                // shown on the wall at a fraction of their previous bandwidth.
                <Banner
                  status="success"
                  title={`${learnedSubStreams} camera${learnedSubStreams === 1 ? "" : "s"} learned a sub-stream path`}
                  description="Cameras imported before this was recorded had no second stream to fall back on. They can now be shown at low resolution, which costs a fraction of the main stream's bandwidth over a site link."
                />
              )}

              {keptLocal.length > 0 && (
                <Banner
                  status="info"
                  title={`${keptLocal.length} camera${keptLocal.length === 1 ? " was" : "s were"} renamed here, so the recorder's label was not applied`}
                  description={keptLocal
                    .map((r) => `ch${r.channel_id}: kept "${r.old_name}" (recorder says "${r.new_name}")`)
                    .join(" · ")}
                />
              )}

              {newChannels.length > 0 && (
                <Banner
                  status="info"
                  title={`${newChannels.length} channel${newChannels.length === 1 ? "" : "s"} on the recorder ${newChannels.length === 1 ? "is" : "are"} not imported`}
                  description={newChannels.map((c) => `ch${c.channel_id}: ${c.name || "unnamed"}`).join(" · ")}
                />
              )}

              {missing.length > 0 && (
                // Warning, not error, and deliberately not an offer to delete.
                // This recorder's own inventory was seen dropping channels and
                // getting them back within an afternoon; absence from one scan
                // is not evidence a camera is gone.
                <Banner
                  status="warning"
                  title={`${missing.length} camera${missing.length === 1 ? "" : "s"} no longer reported by the recorder`}
                  description={`${missing
                    .map((m) => `ch${m.channel_id}: ${m.name}`)
                    .join(" · ")} — left in place. A recorder can drop a channel temporarily, so check the device before removing anything.`}
                />
              )}

              {renamed.length === 0 &&
                keptLocal.length === 0 &&
                newChannels.length === 0 &&
                missing.length === 0 &&
                learnedSubStreams === 0 && (
                  <Text color="secondary">Everything matches the recorder. Nothing to change.</Text>
                )}
            </VStack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} justify="end">
              <Button label="Close" variant="ghost" onClick={() => onOpenChange(false)} />
              {newChannels.length > 0 && (
                <Button
                  label={`Import ${newChannels.length} channel${newChannels.length === 1 ? "" : "s"}`}
                  variant="primary"
                  onClick={onImportNew}
                />
              )}
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
